import React, { useRef } from "react"
import PropTypes from "prop-types"
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Stack,
  IconButton,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpensesInput from "./ExpensesInput"
import ExpensesPartidaItem from "./ExpensesPartidaItem"
import { useLocation } from "react-router-dom"

function hasAnySelected(chapter, selectedItems) {
  if (selectedItems[chapter.chapterId]) return true
  if (
    Array.isArray(chapter.chapters) &&
    chapter.chapters.some(sub => hasAnySelected(sub, selectedItems))
  )
    return true
  if (
    Array.isArray(chapter.partidas) &&
    chapter.partidas.some(partida =>
      isPartidaOrDecompSelected(partida, selectedItems),
    )
  )
    return true
  return false
}

function isPartidaOrDecompSelected(partida, selectedItems) {
  if (selectedItems[partida.partidaId]) return true
  if (Array.isArray(partida.decomposition)) {
    return partida.decomposition.some(
      (_, idx) => selectedItems[`${partida.partidaId}_${idx}`],
    )
  }
  return false
}
function collectSelectedPartidasInTree(chapter, selectedItems, list = []) {
  if (Array.isArray(chapter.partidas)) {
    chapter.partidas.forEach(partida => {
      if (isPartidaOrDecompSelected(partida, selectedItems)) list.push(partida)
    })
  }
  if (Array.isArray(chapter.chapters)) {
    chapter.chapters.forEach(sub =>
      collectSelectedPartidasInTree(sub, selectedItems, list),
    )
  }
  return list
}

// Distribute a total (float) into `n` parts with cent precision so the sum equals the total
function distributeValueIntoParts(total, n) {
  if (n <= 0) return []
  const totalCents = Math.round((Number(total) || 0) * 100)
  const base = Math.floor(totalCents / n)
  const remainder = totalCents % n
  const parts = Array.from({ length: n }, (_, i) => {
    const extra = remainder > 0 && i >= n - remainder ? 1 : 0
    return (base + extra) / 100
  })
  return parts
}
const ExpensesTreeAccordion = ({
  chapter,
  selectedItems,
  allocation,
  setAllocation,
  expandedAccordions,
  setExpandedAccordions,
}) => {
  const location = useLocation()
  const projectId = location.pathname.split("/")[2]

  const chapterKey = `${chapter.chapterId}_c`
  const isChapterSelected = !!selectedItems[chapterKey]
  const selectedSubchapters = Array.isArray(chapter.chapters)
    ? chapter.chapters.filter(sub => hasAnySelected(sub, selectedItems))
    : []

  const selectedPartidas = collectSelectedPartidasInTree(chapter, selectedItems)
  if (
    !isChapterSelected &&
    selectedSubchapters.length === 0 &&
    selectedPartidas.length === 0
  )
    return null

  const expanded = expandedAccordions[chapterKey] ?? isChapterSelected
  const toggleChapterExpanded = isExpanded => {
    setExpandedAccordions(prev => ({
      ...prev,
      [chapterKey]: isExpanded,
    }))
  }

  // refs to detect recent child edits vs chapter edits to avoid loops
  const lastChildEdit = useRef(0)
  const lastChapterEdit = useRef(0)
  const markChildEdit = () => {
    lastChildEdit.current = Date.now()
  }

  let sumPartidas = 0
  if (selectedPartidas.length > 0) {
    sumPartidas = selectedPartidas.reduce(
      (acc, partida) => acc + (allocation?.[partida.partidaId]?.allocated || 0),
      0,
    )
  }

  const handleInputChange = values => {
    const newValue = values.floatValue || 0

    if (Math.abs(newValue - sumPartidas) < 0.0005) {
      setAllocation(prev => ({
        ...prev,
        [chapterKey]: { ...(prev[chapterKey] || {}), allocated: newValue },
      }))
      return
    }

    if (
      lastChildEdit.current > (lastChapterEdit.current || 0) &&
      Date.now() - lastChildEdit.current < 50
    ) {
      setAllocation(prev => ({
        ...prev,
        [chapterKey]: { ...(prev[chapterKey] || {}), allocated: newValue },
      }))
      return
    }

    const partidas = selectedPartidas || []
    const count = partidas.length
    setAllocation(prev => {
      const next = { ...prev }
      next[chapterKey] = { ...(next[chapterKey] || {}), allocated: newValue }

      if (count === 0) return next

      const distributed = distributeValueIntoParts(newValue, count)

      partidas.forEach((partida, idx) => {
        const partVal = distributed[idx] || 0
        const existing = next[partida.partidaId] || {}

        const decompLength = Array.isArray(partida.decompositions)
          ? partida.decompositions.length
          : 0

        const partidaExpanded = !!(
          expandedAccordions && expandedAccordions[partida.partidaId]
        )

        if (decompLength > 0) {
          const selectedDecompIndices = partida.decompositions
            .map((d, i) => i)
            .filter(i => !!selectedItems[`${partida.partidaId}_${i}`])

          if (selectedDecompIndices.length > 0 && partidaExpanded) {
            const decompDistributed = distributeValueIntoParts(
              partVal,
              selectedDecompIndices.length,
            )
            const newDecomp = Array.from({ length: decompLength }, (_, i) => {
              const selIdx = selectedDecompIndices.indexOf(i)
              return selIdx >= 0
                ? decompDistributed[selIdx]
                : (existing.decomposition?.[i] ?? 0)
            })
            next[partida.partidaId] = {
              ...existing,
              projectId: partida.projectId || existing.projectId,
              allocated: newDecomp.reduce((a, b) => a + (b || 0), 0),
              decomposition: newDecomp,
            }
            return
          }

          // if accordion closed or no selected decomps: decomposition stays zeros and partida takes the full partVal
          next[partida.partidaId] = {
            ...existing,
            projectId: partida.projectId || existing.projectId,
            allocated: partVal,
            decomposition: Array.from({ length: decompLength }, () => 0),
          }
          return
        }

        // no decomposition: assign value to partida
        next[partida.partidaId] = {
          ...existing,
          projectId: partida.projectId || existing.projectId,
          allocated: partVal,
          decomposition: existing.decomposition || [],
        }
      })
      lastChapterEdit.current = Date.now()

      return next
    })
  }

  return (
    <Accordion
      sx={{
        mb: 1,
        boxShadow: "none",
        border: "1px solid",
        borderColor: "divider",
      }}
      expanded={expanded}
    >
      <AccordionSummary
        onClick={e => e.stopPropagation()}
        expandIcon={
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation()
              toggleChapterExpanded(!expanded)
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        }
        aria-controls={`expenses-panel-${chapter.chapterId}-content`}
        id={`expenses-panel-${chapter.chapterId}-header`}
        sx={{
          backgroundColor: "grey.300",
          "&:hover": { backgroundColor: "grey.350" },
          minHeight: "36px !important",
          height: "36px",
          paddingInline: 1,
          flexDirection: "row-reverse",
          "&.Mui-expanded": {
            minHeight: "36px !important",
            height: "36px",
            marginBottom: "4px",
          },
          "& .MuiAccordionSummary-content": {
            margin: "6px 0 !important",
            minWidth: 0,
            flex: 1,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: "medium",
              fontSize: "0.9rem",
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
              width: "100%",
              maxWidth: "100%",
            }}
          >
            {chapter.name}
          </Typography>
          <Box sx={{ ml: 2, minWidth: 70 }}>
            <ExpensesInput
              value={sumPartidas}
              onChange={handleInputChange}
              size="small"
            />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1, pt: 0.5 }}>
        <Stack spacing={0.5}>
          {selectedSubchapters.map(sub => (
            <ExpensesTreeAccordion
              key={sub.chapterId}
              chapter={sub}
              selectedItems={selectedItems}
              allocation={allocation}
              setAllocation={setAllocation}
              expandedAccordions={expandedAccordions}
              setExpandedAccordions={setExpandedAccordions}
            />
          ))}
          {selectedPartidas.map(partida => (
            <ExpensesPartidaItem
              key={partida.partidaId}
              partida={partida}
              selectedItems={selectedItems}
              allocation={allocation}
              setAllocation={setAllocation}
              onChildEdit={markChildEdit}
              expandedAccordions={expandedAccordions}
              setExpandedAccordions={setExpandedAccordions}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

ExpensesTreeAccordion.propTypes = {
  chapter: PropTypes.shape({
    chapterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    name: PropTypes.string.isRequired,
    chapters: PropTypes.array,
    partidas: PropTypes.array,
  }).isRequired,
  selectedItems: PropTypes.object.isRequired,
  allocation: PropTypes.object.isRequired,
  setAllocation: PropTypes.func.isRequired,
  expandedAccordions: PropTypes.object,
  setExpandedAccordions: PropTypes.func,
}

export default ExpensesTreeAccordion
